import { useEffect } from "react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAuthStore } from "@/stores/auth-store"
import * as authApi from "@/api/auth"
import { queryClient } from "@/lib/query-client"

export function useMe() {
  const setUser = useAuthStore((state) => state.setUser)

  const query = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      const response = await authApi.getMe()
      return response.data
    },
    retry: false,
  })

  const userData = query.data

  useEffect(() => {
    if (userData) {
      setUser(userData)
    }
  }, [userData, setUser])

  return query
}

export function useLogin() {
  const setUser = useAuthStore((state) => state.setUser)
  const setTwoFactorToken = useAuthStore((state) => state.setTwoFactorToken)

  return useMutation({
    mutationFn: ({ email, password, cf_turnstile_response }: { email: string; password: string; cf_turnstile_response?: string }) =>
      authApi.login(email, password, cf_turnstile_response),
    onSuccess: (response) => {
      if (response.data && "two_factor_required" in response.data) {
        setTwoFactorToken(response.data.two_factor_token, response.data.two_factor_setup_required)
      } else if (response.data && "user" in response.data) {
        setUser(response.data.user)
        queryClient.invalidateQueries({ queryKey: ["auth"] })
      }
    },
  })
}

export function useVerifyTwoFactor() {
  const setUser = useAuthStore((state) => state.setUser)
  const clearTwoFactor = useAuthStore((state) => state.clearTwoFactor)

  return useMutation({
    mutationFn: ({ twoFactorToken, code, rememberDevice }: { twoFactorToken: string; code: string; rememberDevice?: boolean }) =>
      authApi.verifyTwoFactor(twoFactorToken, code, rememberDevice),
    onSuccess: (response) => {
      if (response.data) {
        setUser(response.data.user)
        clearTwoFactor()
        queryClient.invalidateQueries({ queryKey: ["auth"] })
      }
    },
  })
}

export function useLogout() {
  const setUser = useAuthStore((state) => state.setUser)

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      setUser(null)
      queryClient.clear()
    },
  })
}

export function useRegister() {
  return useMutation({
    mutationFn: authApi.register,
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: ({ email, cf_turnstile_response }: { email: string; cf_turnstile_response?: string }) => authApi.forgotPassword(email, cf_turnstile_response),
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: authApi.resetPassword,
  })
}

export function useUpdateProfile() {
  const setUser = useAuthStore((state) => state.setUser)

  return useMutation({
    mutationFn: authApi.updateProfile,
    onSuccess: (response) => {
      if (response.data) {
        setUser(response.data)
      }
      queryClient.invalidateQueries({ queryKey: ["auth"] })
    },
  })
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: authApi.updatePassword,
  })
}

export function useUpdateAvatar() {
  const setUser = useAuthStore((state) => state.setUser)

  return useMutation({
    mutationFn: authApi.updateAvatar,
    onSuccess: (response) => {
      if (response.data) {
        setUser(response.data)
      }
      queryClient.invalidateQueries({ queryKey: ["auth"] })
    },
  })
}
